import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import WorkPageHeader from '../../components/work/WorkPageHeader';
import {
  Alert,
  Button,
  Card,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Result,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Typography,
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  GlobalOutlined,
  EyeOutlined,
  KeyOutlined,
  ControlOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  SendOutlined,
  InstagramOutlined,
} from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError, ForbiddenError } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { STRATEGY_LABELS } from '../../components/live-chat/liveChatMeta';
import MessengerBotsCard from '../../components/live-chat/MessengerBotsCard';
import InstagramAccountsCard from '../../components/live-chat/InstagramAccountsCard';
import OperatorAvatarSetting from '../../components/live-chat/OperatorAvatarSetting';
import WidgetApiSetting from '../../components/live-chat/WidgetApiSetting';

/** فیلدهای قابل ذخیره تنظیمات (عمومی + ویجت) — هر دو تب با یک دکمه ذخیره می‌شوند */
const SETTING_FIELDS = [
  'is_enabled',
  'bot_proxy',
  'test_mode_enabled',
  'auto_assign',
  'assignment_strategy',
  'max_concurrent_chats',
  'welcome_message',
  'offline_message',
  'widget_enabled',
  'widget_title',
  'widget_subtitle',
  'widget_color',
  'widget_position',
  'widget_require_name',
  'widget_require_phone',
  'widget_allowed_domains',
  'widget_ask_department',
  'widget_department_prompt',
  'widget_department_ids',
];

/** تب‌های اتصال منابع — هر کدام ذخیره‌ی مستقل دارند و دکمه ذخیره تنظیمات در آن‌ها نمایش داده نمی‌شود */
const CONNECTION_TABS = ['bale', 'telegram', 'instagram'];

const REQUIREMENT_OPTIONS = [
  { value: 'required', label: <span className="custom-font">اجباری</span> },
  { value: 'optional', label: <span className="custom-font">اختیاری (پرسیده نمی‌شود)</span> },
];

// Segmented فقط مقدار رشته/عدد می‌پذیرد؛ مقدار فرم boolean می‌ماند
const requirementItemProps = {
  getValueProps: (value) => ({ value: value ? 'required' : 'optional' }),
  normalize: (value) => value === 'required',
};

/** پیش‌نمایش زنده ویجت بر اساس مقادیر فرم */
const WidgetPreview = ({ form, departments = [] }) => {
  const color = Form.useWatch('widget_color', form) || '#0D887E';
  const title = Form.useWatch('widget_title', form) || 'پشتیبانی آنلاین';
  const subtitle = Form.useWatch('widget_subtitle', form);
  const requirePhone = Form.useWatch('widget_require_phone', form);
  const requireName = Form.useWatch('widget_require_name', form);
  const welcome = Form.useWatch('welcome_message', form);
  const position = Form.useWatch('widget_position', form) || 'right';
  const enabled = Form.useWatch('widget_enabled', form);
  const askDepartment = Form.useWatch('widget_ask_department', form);
  const departmentIds = Form.useWatch('widget_department_ids', form) || [];
  const prompt = Form.useWatch('widget_department_prompt', form);
  const shownDepartments = askDepartment
    ? departments.filter((d) => !departmentIds.length || departmentIds.includes(d.id))
    : [];
  const showForm = requirePhone || requireName || shownDepartments.length > 0;

  return (
    <div className="livechat-panel p-4 relative overflow-hidden" style={{ minHeight: 420, opacity: enabled === false ? 0.45 : 1 }}>
      <div className="text-xs text-gray-400 mb-3 custom-font">پیش‌نمایش در سایت</div>
      <div
        className="rounded-2xl overflow-hidden shadow-lg bg-white mx-auto"
        style={{ width: 290, marginInlineStart: position === 'left' ? 0 : 'auto', marginInlineEnd: position === 'left' ? 'auto' : 0 }}
      >
        <div className="p-4 text-white" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
          <div className="font-bold custom-font">{title}</div>
          <div className="text-[11px] opacity-90 custom-font">{subtitle || 'معمولاً در چند دقیقه پاسخ می‌دهیم'}</div>
        </div>
        <div className="p-4 flex flex-col gap-2 custom-font" style={{ background: `linear-gradient(180deg, ${color}10, #fff)`, minHeight: 200 }}>
          {showForm ? (
            <>
              <div className="text-[12px] text-gray-500">{welcome || 'سلام! برای شروع گفتگو لطفاً اطلاعات زیر را وارد کنید.'}</div>
              {shownDepartments.length > 0 && (
                <>
                  <div className="text-[11px] font-bold text-gray-700">{prompt || 'لطفاً بخش مورد نظر خود را انتخاب کنید:'}</div>
                  {shownDepartments.map((d, i) => (
                    <div
                      key={d.id}
                      className="h-8 rounded-lg border bg-white text-[11px] font-bold px-3 flex items-center gap-2"
                      style={{ borderColor: i === 0 ? color : `${color}40`, color: i === 0 ? color : '#1e293b' }}
                    >
                      <span className="w-2 h-2 rounded-full border-2" style={{ borderColor: color, background: i === 0 ? color : 'transparent' }} />
                      {d.name}
                    </div>
                  ))}
                </>
              )}
              {(requireName || requirePhone) && (
                <>
                  <div className="text-[11px] text-gray-600">نام{requireName ? <span className="text-red-500"> *</span> : null}</div>
                  <div className="h-8 rounded-lg border border-gray-200 bg-white" />
                </>
              )}
              {requirePhone && (
                <>
                  <div className="text-[11px] text-gray-600">شماره موبایل<span className="text-red-500"> *</span></div>
                  <div className="h-8 rounded-lg border border-gray-200 bg-white text-[11px] text-gray-300 px-2 leading-8" dir="ltr">09123456789</div>
                </>
              )}
              {(requireName || requirePhone) && (
                <div className="h-9 rounded-lg text-white text-xs font-bold flex items-center justify-center mt-1" style={{ background: color }}>
                  شروع گفتگو
                </div>
              )}
            </>
          ) : (
            <>
              <div className="self-end max-w-[80%] rounded-2xl rounded-es-sm bg-white border border-gray-100 px-3 py-2 text-xs">
                {welcome || 'سلام! چطور می‌توانیم کمکتان کنیم؟'}
              </div>
              <div className="self-start max-w-[80%] rounded-2xl rounded-ee-sm px-3 py-2 text-xs text-white" style={{ background: color }}>
                سلام، سؤالی داشتم
              </div>
            </>
          )}
        </div>
      </div>
      <div
        className="absolute bottom-4 w-12 h-12 rounded-full shadow-lg"
        style={{ background: color, [position === 'left' ? 'left' : 'right']: 16 }}
      />
    </div>
  );
};

const LiveChatSettings = () => {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [strategies, setStrategies] = useState([]);
  const [widget, setWidget] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [keyLoading, setKeyLoading] = useState(false);
  // پراکسی ذخیره‌شده ربات‌ها (نه مقدار در حال ویرایش فرم)
  const [botProxySaved, setBotProxySaved] = useState(false);
  const [proxyTest, setProxyTest] = useState({ loading: false, result: null });
  const [form] = Form.useForm();

  const applySettings = useCallback(
    (settings) => {
      form.setFieldsValue({
        ...settings,
        widget_color: settings?.widget_color || '#0D887E',
        widget_position: settings?.widget_position || 'right',
        widget_allowed_domains: settings?.widget_allowed_domains ?? [],
        widget_department_ids: settings?.widget_department_ids ?? [],
        // رمز پراکسی پوشیده برمی‌گردد؛ ارسال همان مقدار، رمز ذخیره‌شده را تغییر نمی‌دهد
        bot_proxy: settings?.bot_proxy_masked ?? '',
      });
      setBotProxySaved(Boolean(settings?.bot_proxy_masked));
      setDirty(false);
    },
    [form],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, depsRes] = await Promise.all([
        liveChatApi.getSettings(),
        liveChatApi.getDepartments().catch(() => null),
      ]);
      setDepartments((depsRes?.data ?? []).filter((d) => d.is_active));
      const data = settingsRes?.data ?? {};
      applySettings(data.settings ?? {});
      setWidget(data.widget ?? null);
      setStrategies(data.strategies ?? []);
    } catch (error) {
      if (error instanceof ForbiddenError || error?.status === 403) setForbidden(true);
      else message.error(error?.message || 'خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await form.validateFields();
      const all = form.getFieldsValue(true);
      const payload = Object.fromEntries(SETTING_FIELDS.filter((k) => k in all).map((k) => [k, all[k] ?? null]));
      payload.widget_department_ids = payload.widget_department_ids || [];
      payload.widget_allowed_domains = (payload.widget_allowed_domains || [])
        .map((d) => String(d).trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, ''))
        .filter(Boolean);
      setSaving(true);
      const res = await liveChatApi.updateSettings(payload);
      if (res?.data?.settings) applySettings(res.data.settings);
      if (res?.data?.widget) setWidget(res.data.widget);
      message.success('تنظیمات ذخیره شد');
    } catch (error) {
      if (error?.errorFields) {
        message.warning('لطفاً خطاهای فرم را برطرف کنید');
        return;
      }
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  /** تست مقدار فعلی فیلد پراکسی (حتی ذخیره‌نشده) با API تلگرام و بله، در مقایسه با اتصال مستقیم */
  const testProxy = async () => {
    setProxyTest({ loading: true, result: null });
    try {
      const res = await liveChatApi.testBotProxy(form.getFieldValue('bot_proxy'));
      setProxyTest({ loading: false, result: res?.data ?? null });
    } catch (error) {
      setProxyTest({ loading: false, result: null });
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در تست پراکسی');
    }
  };

  const regenerateKey = async () => {
    setKeyLoading(true);
    try {
      const res = await liveChatApi.regenerateWidgetKey();
      setWidget(res?.data ?? null);
      message.success('کلید جدید ساخته شد؛ کد نصب را در سایت جایگزین کنید');
    } catch (error) {
      message.error(error?.message || 'خطا در ساخت کلید جدید');
    } finally {
      setKeyLoading(false);
    }
  };

  if (forbidden) {
    return (
      <MainLayout>
        <Result status="403" title={<span className="custom-font">دسترسی به تنظیمات گفتگوی آنلاین ندارید</span>} />
      </MainLayout>
    );
  }

  const generalTab = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
      <Form.Item name="is_enabled" label="ماژول فعال است" valuePropName="checked" extra="در حالت غیرفعال، ویجت سایت و وب‌هوک منابع پیامی نمی‌پذیرند.">
        <Switch />
      </Form.Item>
      <Form.Item name="test_mode_enabled" label="حالت تست (شبیه‌ساز مشتری)" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="auto_assign" label="ارجاع خودکار گفتگوی جدید" valuePropName="checked" extra="فقط به اپراتورهای آنلاینی که به منبع گفتگو دسترسی دارند.">
        <Switch />
      </Form.Item>
      <Form.Item name="assignment_strategy" label="روش ارجاع خودکار">
        <Select options={strategies.map((s) => ({ value: s, label: STRATEGY_LABELS[s] || s }))} />
      </Form.Item>
      <Form.Item name="max_concurrent_chats" label="سقف پیش‌فرض گفتگوی هم‌زمان هر اپراتور" rules={[{ required: true, message: 'سقف را وارد کنید' }]}>
        <InputNumber min={1} max={100} className="w-full" />
      </Form.Item>
      <div />
      <Form.Item name="welcome_message" label="پیام خوش‌آمد" extra="اولین پیامی که مشتری در ویجت سایت می‌بیند.">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} placeholder="سلام! چطور می‌توانیم کمکتان کنیم؟" />
      </Form.Item>
      <Form.Item name="offline_message" label="پیام آفلاین" extra="وقتی هیچ اپراتوری آنلاین نیست در ویجت نمایش داده می‌شود.">
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} placeholder="الان آنلاین نیستیم؛ پیام بگذارید، به‌زودی پاسخ می‌دهیم." />
      </Form.Item>
      <Form.Item
        name="bot_proxy"
        label="پراکسی ربات‌های بله و تلگرام"
        extra="درخواست‌های ربات‌هایی که «استفاده از پراکسی» در آن‌ها روشن است از این پراکسی عبور می‌کند. HTTP یا SOCKS؛ خالی = بدون پراکسی."
      >
        <Input
          dir="ltr"
          allowClear
          maxLength={500}
          placeholder="http://127.0.0.1:10809 یا socks5h://user:pass@host:1080"
          onChange={() => setProxyTest({ loading: false, result: null })}
          addonAfter={
            <Button type="link" size="small" icon={<ExperimentOutlined />} loading={proxyTest.loading} onClick={testProxy} className="custom-font">
              تست
            </Button>
          }
        />
      </Form.Item>
      {proxyTest.result && (
        <div className="md:col-span-2 mb-4">
          <Alert
            type={proxyTest.result.ok ? 'success' : 'error'}
            showIcon
            className="custom-font"
            title={proxyTest.result.ok ? `پراکسی کار می‌کند (${proxyTest.result.proxy})` : `اتصال از طریق پراکسی برقرار نشد (${proxyTest.result.proxy})`}
            description={
              <div className="flex flex-col gap-1 mt-1">
                {proxyTest.result.results.map((r) => (
                  <div key={`${r.target}-${r.via}`} className="text-xs flex items-start gap-2">
                    {r.ok ? <CheckCircleOutlined className="text-green-600 mt-0.5" /> : <CloseCircleOutlined className="text-red-500 mt-0.5" />}
                    <span className="whitespace-nowrap">
                      {r.target} {r.via === 'proxy' ? 'از طریق پراکسی' : 'بدون پراکسی'}:
                    </span>
                    <span dir="ltr" className="break-all text-gray-600 dark:text-gray-300">
                      {r.ok ? `HTTP ${r.status} · ${r.ms}ms` : `${r.error} · ${r.ms}ms`}
                    </span>
                  </div>
                ))}
                {proxyTest.result.suggestion && (
                  <div className="text-xs mt-2">
                    با <code dir="ltr">{proxyTest.result.suggestion}</code> (resolve نام دامنه در خود پراکسی) اتصال برقرار شد؛ DNS این سرور آدرس
                    تلگرام را فیلتر کرده است.{' '}
                    <Button
                      size="small"
                      type="link"
                      className="custom-font p-0"
                      onClick={() => {
                        const current = String(form.getFieldValue('bot_proxy') || '');
                        // فقط پروتکل عوض می‌شود تا رمز (حتی پوشیده) دست نخورد؛ مقدار پوشیده جدید هم سمت سرور به رمز ذخیره‌شده نگاشت می‌شود
                        form.setFieldValue('bot_proxy', current.replace(/^socks5:\/\//i, 'socks5h://').replace(/^socks4:\/\//i, 'socks4a://'));
                        setDirty(true);
                        setProxyTest({ loading: false, result: null });
                      }}
                    >
                      استفاده از این آدرس
                    </Button>
                  </div>
                )}
              </div>
            }
          />
        </div>
      )}
    </div>
  );

  const widgetTab = (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
      <div>
        <div className="mb-5">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 custom-font">
            کد نصب (قبل از تگ &lt;/body&gt; در همه صفحات سایت)
          </div>
          <div className="livechat-panel p-3 flex items-start gap-3">
            <Typography.Paragraph
              copyable={widget?.embed_code ? { text: widget.embed_code, tooltips: ['کپی', 'کپی شد'] } : false}
              className="!mb-0 flex-1 text-xs livechat-embed-code"
              style={{ wordBreak: 'break-all' }}
            >
              {widget?.embed_code || '—'}
            </Typography.Paragraph>
            <Space direction="vertical" size={6}>
              {widget?.demo_url && (
                <Button size="small" icon={<EyeOutlined />} href={widget.demo_url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'CustomFont' }}>
                  صفحه نمونه
                </Button>
              )}
              <Popconfirm title="کلید ویجت عوض شود؟" description="کد نصب فعلی روی سایت‌ها از کار می‌افتد." okText="بله" cancelText="خیر" onConfirm={regenerateKey}>
                <Button size="small" icon={<KeyOutlined />} loading={keyLoading} style={{ fontFamily: 'CustomFont' }}>
                  کلید جدید
                </Button>
              </Popconfirm>
            </Space>
          </div>
        </div>

        <WidgetApiSetting widget={widget} active={activeTab === 'widget'} onChange={setWidget} />

        <div className="livechat-panel p-4 mb-5">
          <div className="font-bold text-gray-800 dark:text-gray-100 mb-1 custom-font">اطلاعات مشتری قبل از شروع گفتگو</div>
          <div className="text-xs text-gray-500 mb-4 custom-font">
            اگر اجباری باشد، ویجت قبل از اولین پیام فرم را نشان می‌دهد و تا تکمیل آن، پیام ارسال نمی‌شود (سمت سرور هم بررسی می‌شود).
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <Form.Item name="widget_require_phone" label="شماره موبایل" className="!mb-2" {...requirementItemProps}>
              <Segmented options={REQUIREMENT_OPTIONS} />
            </Form.Item>
            <Form.Item name="widget_require_name" label="نام" className="!mb-2" {...requirementItemProps}>
              <Segmented options={REQUIREMENT_OPTIONS} />
            </Form.Item>
          </div>
        </div>

        <div className="livechat-panel p-4 mb-5">
          <div className="font-bold text-gray-800 dark:text-gray-100 mb-1 custom-font">انتخاب بخش قبل از شروع گفتگو</div>
          <div className="text-xs text-gray-500 mb-4 custom-font">
            بازدیدکننده پیش از اولین پیام یکی از بخش‌ها (مثلاً پشتیبانی یا فروش) را انتخاب می‌کند و گفتگو فقط به اپراتورهای همان بخش می‌رسد.
          </div>
          <Form.Item name="widget_ask_department" valuePropName="checked" className="!mb-3">
            <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.widget_ask_department !== b.widget_ask_department}>
            {({ getFieldValue }) =>
              getFieldValue('widget_ask_department') ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  {departments.length === 0 && (
                    <Alert type="warning" showIcon className="custom-font mb-3 md:col-span-2" title="ابتدا در تب «بخش‌ها» حداقل یک بخش فعال تعریف کنید." />
                  )}
                  <Form.Item name="widget_department_prompt" label="متن درخواست انتخاب">
                    <Input maxLength={300} placeholder="لطفاً بخش مورد نظر خود را انتخاب کنید:" />
                  </Form.Item>
                  <Form.Item name="widget_department_ids" label="بخش‌های قابل انتخاب" extra="خالی = همه بخش‌های فعال">
                    <Select mode="multiple" allowClear options={departments.map((d) => ({ value: d.id, label: d.name }))} />
                  </Form.Item>
                </div>
              ) : null
            }
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <Form.Item name="widget_enabled" label="ویجت فعال است" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="widget_position" label="محل نمایش دکمه گفتگو">
            <Segmented
              options={[
                { value: 'right', label: <span className="custom-font">پایین راست</span> },
                { value: 'left', label: <span className="custom-font">پایین چپ</span> },
              ]}
            />
          </Form.Item>
          <Form.Item name="widget_title" label="عنوان ویجت">
            <Input maxLength={100} placeholder="پشتیبانی آنلاین" />
          </Form.Item>
          <Form.Item name="widget_subtitle" label="زیرعنوان (اختیاری)">
            <Input maxLength={191} placeholder="معمولاً در چند دقیقه پاسخ می‌دهیم" />
          </Form.Item>
          <Form.Item name="widget_color" label="رنگ اصلی" getValueFromEvent={(color) => color?.toHexString?.() ?? color}>
            <ColorPicker
              showText
              disabledAlpha
              presets={[{ label: 'رنگ‌های پیشنهادی', colors: ['#0D887E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#111827'] }]}
            />
          </Form.Item>
          <Form.Item
            name="widget_allowed_domains"
            label="دامنه‌های مجاز (اختیاری)"
            extra="خالی = همه سایت‌ها. هر دامنه زیردامنه‌هایش را هم شامل می‌شود. برای تست محلی localhost را اضافه کنید."
          >
            <Select mode="tags" tokenSeparators={[',', ' ']} placeholder="example.com" open={false} suffixIcon={null} />
          </Form.Item>
          <OperatorAvatarSetting widget={widget} onChange={setWidget} />
        </div>
        <Alert
          type="info"
          showIcon
          className="custom-font"
          title="برای کاربران لاگین‌شده سایت، window.KarmadChat.setUser({ name, phone }) را صدا بزنید تا فرم نمایش داده نشود."
        />
      </div>
      <WidgetPreview form={form} departments={departments} />
    </div>
  );

  const baleTab = <MessengerBotsCard platform="bale" proxyAvailable={botProxySaved} />;
  const telegramTab = <MessengerBotsCard platform="telegram" proxyAvailable={botProxySaved} />;
  const instagramTab = <InstagramAccountsCard proxyAvailable={botProxySaved} />;

  return (
    <MainLayout>
      <div style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0 work-page-card" styles={{ body: { padding: 0 } }}>
          <WorkPageHeader
            title="تنظیمات گفتگوی آنلاین"
            subtitle="ارجاع، ویجت سایت و اتصال منابع"
            icon={SettingOutlined}
            headerExtra={
              !CONNECTION_TABS.includes(activeTab) ? (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={save}
                  loading={saving}
                  className={PANEL_PRIMARY_BTN_CLASS}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  {dirty ? 'ذخیره تغییرات' : 'ذخیره'}
                </Button>
              ) : null
            }
          />
          <div className="px-6 pb-6 work-page-body rounded-b-xl">
            <Spin spinning={loading}>
              <Form form={form} layout="vertical" className="custom-font" onValuesChange={() => setDirty(true)}>
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    { key: 'general', label: <span className="custom-font"><ControlOutlined /> عمومی</span>, children: generalTab, forceRender: true },
                    { key: 'widget', label: <span className="custom-font"><GlobalOutlined /> ویجت سایت</span>, children: widgetTab, forceRender: true },
                    { key: 'bale', label: <span className="custom-font"><CommentOutlined /> بله</span>, children: baleTab },
                    { key: 'telegram', label: <span className="custom-font"><SendOutlined /> تلگرام</span>, children: telegramTab },
                    { key: 'instagram', label: <span className="custom-font"><InstagramOutlined /> اینستاگرام</span>, children: instagramTab },
                  ]}
                />
              </Form>
              {dirty && !CONNECTION_TABS.includes(activeTab) && (
                <div className="text-xs text-amber-600 custom-font">تغییرات ذخیره نشده دارید.</div>
              )}
            </Spin>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default LiveChatSettings;
